import type { FieldDef } from '../domain/types'

export const digitsOnly = (value: string): string => value.replace(/\D/g, '')

export const maskPhone = (value: string): string => {
  const rawDigits = digitsOnly(value)
  const hasBrazilCountryCode = rawDigits.length > 11 && rawDigits.startsWith('55')
  const digits = (hasBrazilCountryCode ? rawDigits.slice(2) : rawDigits).slice(0, 11)
  if (digits.length <= 2) {return digits ? `(${digits}` : ''}
  const national = digits.length <= 6
    ? `(${digits.slice(0, 2)}) ${digits.slice(2)}`
    : digits.length <= 10
      ? `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
      : `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  return hasBrazilCountryCode ? `+55 ${national}` : national
}

export const maskCpf = (value: string): string => {
  const digits = digitsOnly(value).slice(0, 11)
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2')
}

export const maskCnpj = (value: string): string => {
  const digits = digitsOnly(value).slice(0, 14)
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

export const isValidPhone = (value: string): boolean => {
  const rawDigits = digitsOnly(value)
  const digits = rawDigits.length > 11 && rawDigits.startsWith('55') ? rawDigits.slice(2) : rawDigits
  return digits.length === 10 || digits.length === 11
}

export const isValidEmail = (value: string): boolean => {
  const trimmed = value.trim()
  return !trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

export const isValidCpf = (value: string): boolean => {
  const digits = digitsOnly(value)
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {return false}

  const checkDigit = (length: number): number => {
    let sum = 0
    for (let i = 0; i < length; i += 1) {sum += Number(digits[i]) * (length + 1 - i)}
    const remainder = (sum * 10) % 11
    return remainder === 10 ? 0 : remainder
  }

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10])
}

export const isValidCnpj = (value: string): boolean => {
  const digits = digitsOnly(value)
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) {return false}

  const checkDigit = (base: string): number => {
    const weights = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    let sum = 0
    for (let i = 0; i < base.length; i += 1) {sum += Number(base[i]) * weights[i]}
    const remainder = sum % 11
    return remainder < 2 ? 0 : 11 - remainder
  }

  const d1 = checkDigit(digits.slice(0, 12))
  const d2 = checkDigit(digits.slice(0, 12) + d1)
  return d1 === Number(digits[12]) && d2 === Number(digits[13])
}

// Guard de conveniência: só roda no blur (não a cada tecla), senão atrapalha quem
// está apagando o prefixo pra trocar de domínio. Não duplica se já vier com schema.
export const withHttpScheme = (value: string): string => {
  const trimmed = value.trim()
  return !trimmed || /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

export const formatFieldValue = (def: FieldDef, value: string): string => {
  if (def.type === 'bool') {return value === 'true' || value === 'false' ? value : ''}
  if (def.type === 'tel') {return maskPhone(value)}
  if (def.type === 'cpf') {return maskCpf(value)}
  if (def.type === 'cnpj') {return maskCnpj(value)}
  return value
}

export const fieldValidationMessage = (def: FieldDef, value: string): string | null => {
  if (def.type === 'bool') {
    if (!value) {return def.required ? 'Campo obrigatório.' : null}
    if (value !== 'true' && value !== 'false') {return 'Escolha Sim ou Não.'}
    return null
  }
  if (!value.trim()) {return def.required ? 'Campo obrigatório.' : null}
  if (def.maxLength && value.length > def.maxLength) {return `Máximo de ${def.maxLength} caracteres.`}
  if (def.type === 'tel' && !isValidPhone(value)) {return 'Informe um telefone com 10 ou 11 dígitos.'}
  if (def.type === 'cpf' && !isValidCpf(value)) {return 'Informe um CPF válido.'}
  if (def.type === 'cnpj' && !isValidCnpj(value)) {return 'Informe um CNPJ válido.'}
  if (def.type === 'number' && Number.isNaN(Number(value))) {return 'Informe um valor numérico.'}
  if (def.type === 'url') {
    try { new URL(value) } catch { return 'Informe uma URL válida.' }
  }
  return null
}

export const isFieldValueValid = (def: FieldDef, value: string): boolean =>
  fieldValidationMessage(def, value) == null
